import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Download, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const COVER = "/derive/radio/cover.jpg";
const L = {
  fr: { title: "Invite tes proches à écouter", share: "Partager avec la pochette", copy: "Copier le texte", copied: "Texte copié", dl: "Pochette pour Instagram" },
  en: { title: "Invite your friends to listen", share: "Share with the cover", copy: "Copy the text", copied: "Text copied", dl: "Cover for Instagram" },
  es: { title: "Invita a los tuyos a escuchar", share: "Compartir con la portada", copy: "Copiar el texto", copied: "Texto copiado", dl: "Portada para Instagram" },
  nl: { title: "Nodig je naasten uit om te luisteren", share: "Delen met de hoes", copy: "Tekst kopiëren", copied: "Tekst gekopieerd", dl: "Hoes voor Instagram" },
};

const NETWORKS = [
  { id: "whatsapp", label: "WhatsApp", href: (t, u) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}` },
  { id: "facebook", label: "Facebook", href: (t, u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}&quote=${encodeURIComponent(t)}` },
  { id: "x", label: "X", href: (t, u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}` },
  { id: "linkedin", label: "LinkedIn", href: (t, u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { id: "telegram", label: "Telegram", href: (t, u) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
];

export const SocialInvite = () => {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const [text, setText] = useState("");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/radio?lang=${lang}`;
  useEffect(() => { api.get("/radio/birth", { params: { lang } }).then(({ data }) => setText(data.invite || "")).catch(() => {}); }, [lang]);

  const shareWithCover = async () => {
    try {
      const blob = await (await fetch(COVER)).blob();
      const file = new File([blob], "radio-cocoon.jpg", { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Radio Cocoon", text: `${text} ${url}` });
        return;
      }
      if (navigator.share) { await navigator.share({ title: "Radio Cocoon", text, url }); return; }
      await navigator.clipboard.writeText(`${text} ${url}`); toast.success(T.copied);
    } catch { /* annulé */ }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success(T.copied); } catch { /* */ } };

  return (
    <div className="mt-12 glass rounded-3xl border border-primary/20 p-5 sm:p-7" data-testid="social-invite">
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <img src={COVER} alt="Radio Cocoon" className="w-28 h-28 rounded-2xl object-cover border border-primary/25 shadow-xl flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-[0.24em] text-primary/80 mb-2">{T.title}</p>
          <p className="text-sm text-foreground/75 leading-relaxed mb-4" data-testid="social-invite-text">{text} <span className="text-primary/80">{window.location.host}/radio</span></p>
          <div className="flex flex-wrap gap-2">
            <button onClick={shareWithCover} data-testid="social-invite-share" className="btn-frost-orange inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold">
              <Share2 size={13} /> {T.share}
            </button>
            {NETWORKS.map((n) => (
              <a key={n.id} href={n.href(text, url)} target="_blank" rel="noreferrer" data-testid={`social-invite-${n.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs text-foreground/80 hover:text-primary hover:border-primary/40 transition-colors">
                <Send size={12} /> {n.label}
              </a>
            ))}
            <button onClick={copy} data-testid="social-invite-copy" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs text-foreground/80 hover:text-primary hover:border-primary/40 transition-colors">
              <Copy size={12} /> {T.copy}
            </button>
            <a href={COVER} download="radio-cocoon.jpg" data-testid="social-invite-download" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs text-foreground/80 hover:text-primary hover:border-primary/40 transition-colors">
              <Download size={12} /> {T.dl}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
